import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { Box, Paper, Skeleton, TextField, Typography } from "@mui/material";
import {
  AccessibilityNewOutlined,
  AddCircleOutline,
  ChairOutlined,
  ImageSearchOutlined,
  LocalHospitalOutlined,
  MedicalServicesOutlined,
  ScienceOutlined,
  SpaOutlined,
} from "@mui/icons-material";
import { Button, ChoiceToggle, OptionCardGroup, PageHeader, SectionHeader } from "@components/ui_v2";
import {
  CardResumenCotizacion,
  ResumenCifrasCotizador,
  SliderCotizador,
} from "@components/ui_v2/cotizadoresComponents";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useLazyGetLeasingTypeConfigQuery } from "@api/catalogs";
import { LeasingTypeStructure, TaxSystemConfigResponse } from "@interfaces/index";
import {
  calculateAmountMonthly,
  formatCurrency,
  buildTermOptions,
  getClosestTerm,
  formatCurrencyShort,
  formatCurrencyWhole,
} from "@helpers/funciones";
import { LEASING_TYPE_CODES, PATHS_TOFU_V2 } from "@helpers/constantes";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

interface FormValues {
  equipo: string;
  descripcion: string;
  amount: number;
  term: number;
  advance: number;
}

/** Mercader solo arrienda a PFAE: una sola configuración de tax system. */
const TAX_SYSTEM_CODE = "MER01";

/** Rango del anticipo del diseño (10–50% en pasos de 5, 20% inicial) si la config no lo trae. */
const ADVANCE_FALLBACK = { min: 10, max: 50, step: 5, default: 20 };

const EMPTY_CONFIG: TaxSystemConfigResponse = {
  id: "",
  code: "",
  product_id: "",
  minAmount: 0,
  maxAmount: 0,
  stepAmount: 0,
  minTerm: 0,
  maxTerm: 0,
  stepTerm: 0,
  defaultAmount: 0,
  defaultTerm: 0,
  tax_system_type: "",
};

/**
 * Tipos de equipo del diseño de Mercader, con su ícono (el catálogo no lo trae). El servicio también
 * regresa tipos de otros productos (vehicular, motocicletas): solo se muestran estos códigos.
 */
const EQUIPO_ICONS: Record<string, ReactNode> = {
  [LEASING_TYPE_CODES.IMAGENOLOGIA]: <ImageSearchOutlined fontSize="small" />,
  [LEASING_TYPE_CODES.QUIRURGICO_HOSPITALARIO]: <LocalHospitalOutlined fontSize="small" />,
  [LEASING_TYPE_CODES.DENTAL]: <MedicalServicesOutlined fontSize="small" />,
  [LEASING_TYPE_CODES.LABORATORIO_DIAGNOSTICO]: <ScienceOutlined fontSize="small" />,
  [LEASING_TYPE_CODES.CONSULTORIO_MOBILIARIO]: <ChairOutlined fontSize="small" />,
  [LEASING_TYPE_CODES.ESTETICA_DERMATOLOGIA]: <SpaOutlined fontSize="small" />,
  [LEASING_TYPE_CODES.REHABILITACION_TERAPIA]: <AccessibilityNewOutlined fontSize="small" />,
  [LEASING_TYPE_CODES.OTRO_EQUIPO_MEDICO]: <AddCircleOutline fontSize="small" />,
};

/** Tipos de equipo médico activos del catálogo, en el orden que define backend. */
const toEquipoOptions = (leasingTypes: LeasingTypeStructure[]) =>
  leasingTypes
    .filter((item) => item.active && item.leasing_type?.active !== false && item.leasing_type?.code in EQUIPO_ICONS)
    .sort((a, b) => (a.leasing_type?.order ?? 0) - (b.leasing_type?.order ?? 0))
    .map((item) => ({
      value: item.leasing_type_id,
      label: item.leasing_type.name,
      sub: item.leasing_type.description,
      icon: EQUIPO_ICONS[item.leasing_type.code],
      testId: `cotizador-mercader-equipo-${item.leasing_type.code}`,
    }));

/**
 * La descripción es obligatoria con "Otro equipo médico": sin ella no se puede validar que el equipo sea
 * arrendable (QA COT-02). El equipo se guarda por `leasing_type_id`, así que se recibe el id de ese tipo.
 */
const buildValidationSchema = (otroEquipoId?: string) =>
  yup.object().shape({
    equipo: yup.string().required("Campo requerido"),
    descripcion: yup
      .string()
      .trim()
      .when("equipo", {
        is: (equipo: string) => !!otroEquipoId && equipo === otroEquipoId,
        then: (schema) => schema.required("Describe el equipo: es obligatorio con «Otro equipo médico»."),
      }),
  });

const CotizadorMercader = () => {
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getLeasingTypeConfig] = useLazyGetLeasingTypeConfigQuery();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<TaxSystemConfigResponse | null>(null);
  const [leasingTypes, setLeasingTypes] = useState<LeasingTypeStructure[]>([]);

  const otroEquipoId = leasingTypes.find(
    (item) => item.leasing_type?.code === LEASING_TYPE_CODES.OTRO_EQUIPO_MEDICO,
  )?.leasing_type_id;
  const validationSchema = useMemo(() => buildValidationSchema(otroEquipoId), [otroEquipoId]);

  const formik = useFormik<FormValues>({
    initialValues: {
      // `leasing_type_id` del tipo de equipo; se elige el primero al llegar el catálogo.
      equipo: "",
      descripcion: "",
      amount: 0,
      term: 0,
      advance: ADVANCE_FALLBACK.default,
    },
    validationSchema,
    onSubmit: (values) => {
      if (!cotizadorConfig.product_id || !values.amount || !values.term || !monthlyRent) {
        return;
      }
      const sourceCode = sessionStorage.getItem("source_code");

      // El tipo de equipo viaja como `leasing_type_id`, igual que en `CotizadorVivemoto`.
      // La descripción va en la URL; `CrearCuenta` la deja en el store y `RegistroDatosPersonalesMercader`
      // la guarda en el `details` del leasing.
      let urlPortal = `${PATHS_TOFU_V2.crearCuenta.path}?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${values.amount}&loan_term=${values.term}&tax_system_type=${cotizadorConfig.tax_system_type}&advance_percentage=${values.advance}&leasing_type_id=${values.equipo}`;
      const descripcion = values.descripcion.trim();
      if (descripcion) {
        urlPortal += `&equipment_description=${encodeURIComponent(descripcion)}`;
      }
      if (sourceCode) {
        urlPortal += `&source_code=${sourceCode}`;
      }
      navigate(urlPortal);
    },
  });

  const { values, setFieldValue } = formik;

  const cotizadorConfig = config ?? EMPTY_CONFIG;
  const termOptions = useMemo(
    () => buildTermOptions(cotizadorConfig.minTerm, cotizadorConfig.maxTerm, cotizadorConfig.stepTerm),
    [cotizadorConfig.minTerm, cotizadorConfig.maxTerm, cotizadorConfig.stepTerm],
  );
  const advanceRange = {
    min: cotizadorConfig.minAdvance ?? ADVANCE_FALLBACK.min,
    max: cotizadorConfig.maxAdvance ?? ADVANCE_FALLBACK.max,
    step: cotizadorConfig.stepAdvance ?? ADVANCE_FALLBACK.step,
  };

  // Arrendamiento puro: la renta se calcula sobre el valor menos el anticipo, sin valor residual.
  const advanceAmount = values.amount * (values.advance / 100);
  const financedAmount = values.amount - advanceAmount;
  const monthlyRent = useMemo(
    () =>
      financedAmount && values.term
        ? calculateAmountMonthly(financedAmount, values.term, interfaceConfig?.interest_rate)
        : 0,
    [financedAmount, values.term, interfaceConfig?.interest_rate],
  );

  const getInitialData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getTaxSystemConfig(TAX_SYSTEM_CODE).unwrap();
      setConfig(response);

      // Tipos de equipo del tax system. Aparte del config: si falla, el cotizador sigue funcionando.
      try {
        setLeasingTypes(await getLeasingTypeConfig({ tax_system_id: response.id }).unwrap());
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "CotizadorMercader-leasing_types" } });
      }
    } catch (error) {
      // Igual que Flux: sin config la pantalla queda en ceros, sin modal de error.
      Sentry.captureException(error, { extra: { component: "CotizadorMercader-initial_fetch" } });
    } finally {
      setIsLoading(false);
    }
  }, [getTaxSystemConfig, getLeasingTypeConfig]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const equipoOptions = useMemo(() => toEquipoOptions(leasingTypes), [leasingTypes]);

  // Al llegar el catálogo queda elegido el primer tipo de equipo, como en el diseño.
  useEffect(() => {
    if (!values.equipo && equipoOptions.length > 0) {
      setFieldValue("equipo", equipoOptions[0].value);
    }
  }, [equipoOptions, values.equipo, setFieldValue]);

  // Al llegar la config se toman sus valores por default.
  useEffect(() => {
    const { minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm, defaultAdvance } = cotizadorConfig;

    if (minTerm < maxTerm && stepTerm > 0) {
      setFieldValue("amount", defaultAmount);
      setFieldValue("term", getClosestTerm(buildTermOptions(minTerm, maxTerm, stepTerm), defaultTerm));
      setFieldValue("advance", defaultAdvance ?? ADVANCE_FALLBACK.default);
    }
  }, [cotizadorConfig, setFieldValue]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  const monthlyText = isNaN(monthlyRent) || !isFinite(monthlyRent) ? "$0.00" : formatCurrency(monthlyRent);
  const bloqueado = isLoading || formik.isSubmitting;

  return (
    <Box>
      <PageHeader
        variant="cotizador"
        eyebrow="Arrendamiento de equipo médico"
        title="Estima tu renta mensual"
        subtitle="Arrendamiento puro para consultorios, clínicas y hospitales. Ajusta el valor del equipo, el plazo y tu enganche para ver una estimación de renta."
      />

      <Paper variant="lendia_paper">
        <SectionHeader variant="cotizador" label="Tipo de equipo" first />
        {!isLoading ? (
          <OptionCardGroup
            layout="tiles"
            value={values.equipo}
            onChange={(value) => setFieldValue("equipo", value)}
            options={equipoOptions}
          />
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5 }}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} variant="rounded" animation="wave" height={118} />
            ))}
          </Box>
        )}

        <SectionHeader
          variant="cotizador"
          label="Describe el equipo"
          meta={
            values.equipo && values.equipo === otroEquipoId
              ? "Obligatorio: marca, modelo y proveedor, si ya los tienes"
              : "Marca, modelo y proveedor, si ya los tienes"
          }
        />
        <TextField
          autoComplete="off"
          size="lendia_input"
          fullWidth
          name="descripcion"
          sx={{ "& .MuiOutlinedInput-input": { color: "tokens.textPrimary" } }}
          placeholder="Ej. Ultrasonido Mindray DC-70 nuevo, con dos transductores"
          value={values.descripcion}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={bloqueado}
          error={formik.touched.descripcion && Boolean(formik.errors.descripcion)}
          helperText={(formik.touched.descripcion && formik.errors.descripcion) || undefined}
          inputProps={{ "data-testid": "cotizador-mercader-descripcion-input" }}
        />
        <Typography variant="body3" color="tokens.textTertiary" component="p" sx={{ mt: 1.5 }}>
          Con la descripción validamos que el equipo sea arrendable y estimamos su vida útil. Si aún no eliges
          proveedor, dinos qué buscas y lo cotizamos contigo.
        </Typography>

        <SectionHeader
          variant="cotizador"
          label="Valor del equipo"
          meta={`${formatCurrencyShort(cotizadorConfig.minAmount)} – ${formatCurrencyShort(cotizadorConfig.maxAmount)} MXN · valor de factura sin IVA`}
        />
        <SliderCotizador
          value={values.amount}
          onChange={(value) => setFieldValue("amount", value)}
          min={cotizadorConfig.minAmount}
          max={cotizadorConfig.maxAmount}
          step={cotizadorConfig.stepAmount}
          valueText={formatCurrencyWhole(values.amount)}
          unit="MXN sin IVA"
          minLabel={formatCurrencyShort(cotizadorConfig.minAmount)}
          maxLabel={formatCurrencyShort(cotizadorConfig.maxAmount)}
          loading={isLoading}
          testId="cotizador-mercader-valor-slider"
        />
        <Typography variant="body3" color="tokens.textTertiary" component="p" sx={{ mt: 1.5 }}>
          Captura el valor <strong>sin IVA</strong> que aparece en la cotización o factura de tu proveedor. El IVA no se
          financia: se paga en la renta mes con mes.
        </Typography>

        <SectionHeader variant="cotizador" label="Plazo del arrendamiento" />
        {!isLoading ? (
          <ChoiceToggle
            variant="cotizador"
            value={values.term}
            onChange={(value) => setFieldValue("term", value)}
            options={termOptions.map((term) => ({ value: term, label: `${term} meses` }))}
            testId="cotizador-mercader-plazo"
          />
        ) : (
          <Skeleton variant="rounded" animation="wave" height={50} />
        )}

        <SectionHeader
          variant="cotizador"
          label="Anticipo"
          meta={`${formatCurrencyWhole(advanceAmount)} sin IVA · mínimo ${advanceRange.min}%, máximo ${advanceRange.max}%`}
        />
        <SliderCotizador
          value={values.advance}
          onChange={(value) => setFieldValue("advance", value)}
          min={advanceRange.min}
          max={advanceRange.max}
          step={advanceRange.step}
          valueText={String(values.advance)}
          unit="%"
          minLabel={`${advanceRange.min}%`}
          maxLabel={`${advanceRange.max}%`}
          loading={isLoading}
          testId="cotizador-mercader-anticipo-slider"
        />

        <SectionHeader variant="cotizador" label="Resumen de tu estimación" />
        <ResumenCifrasCotizador
          items={[
            { label: "Valor del equipo", value: formatCurrencyWhole(values.amount) },
            { label: `Enganche (${values.advance}%)`, value: formatCurrencyWhole(advanceAmount) },
            { label: "Monto a financiar", value: formatCurrencyWhole(financedAmount) },
          ]}
        />
        <Typography variant="body3" color="tokens.textTertiary" component="p" sx={{ mt: 1.5 }}>
          <strong>Todas las cifras son sin IVA.</strong> La renta se factura con IVA del 16% y es deducible al 100% como
          gasto de operación.
        </Typography>
      </Paper>

      <CardResumenCotizacion
        label="Renta mensual estimada"
        amount={monthlyText}
        caption={`MXN sin IVA / mes durante ${values.term || 0} meses · enganche de ${formatCurrencyWhole(advanceAmount)} · sin valor residual`}
        disclaimer="Estimación inicial sujeta a aprobación de crédito."
        actions={
          <Button
            tone="hero"
            withArrow
            onClick={formik.submitForm}
            disabled={bloqueado}
            data-testid="cotizador-mercader-continue-button"
          >
            Solicitar arrendamiento
          </Button>
        }
      />
    </Box>
  );
};
export default CotizadorMercader;
